// oxlint-disable complexity, max-statements
import type { QueryClient } from '@tanstack/react-query';
import { merge } from 'lodash-es';

import { localStorage } from '@change-corgi/core/storage';

import type { PetitionDraft } from 'src/app/shared/api/petitionDraft';
import { DraftClient, InMemoryAdapter, LocalStorageAdapter } from 'src/app/shared/api/petitionDraft';
import type { UtilityContextWithTracker } from 'src/app/shared/contexts/asyncData';

import type { StartAPetitionDataQuery } from '../api.graphql';

import { getViewerDeviceId } from './utils';

type Experiments = StartAPetitionDataQuery['experiments'];
function getVariant(experiments: Experiments) {
	if (experiments.starfishPreviewEditExperiment.variation === 'variant_a') return 'preview_edit_variant_a' as const;
	if (experiments.starfishPreviewEditExperiment.variation === 'variant_b') return 'preview_edit_variant_b' as const;
	if (experiments.starfishSap3RecommendedAngles.variation === 'variant_b') return 'recommendations_variant_b' as const;
	return 'control' as const;
}

type FindOrCreateDraftParams = StartAPetitionDataQuery & { draftAuthorId: string };
type FindOrCreateDraftOptions = UtilityContextWithTracker & { queryClient: QueryClient };

export async function findOrCreateDraft(
	{ draftAuthorId, experiments, viewer }: FindOrCreateDraftParams,
	options: FindOrCreateDraftOptions,
): Promise<PetitionDraft> {
	try {
		const draftClient = new DraftClient([
			new LocalStorageAdapter(localStorage),
			new InMemoryAdapter(options.queryClient),
		]);

		const currentDraft = await draftClient.getDraft(draftAuthorId);
		const variant = getVariant(experiments);
		const loginState = viewer.user?.id ? 'AUTHENTICATED' : 'GUEST';
		const deviceId = await getViewerDeviceId(options.userAgent);

		const defaultMetadata = {
			variant,
			user: { id: viewer.user?.id, initialLoginState: loginState },
			deviceId,
		} as const;

		if (!currentDraft) {
			return await draftClient.createDraft(draftAuthorId, { metadata: defaultMetadata });
		}

		const didVariantChange = currentDraft.metadata?.variant && currentDraft.metadata.variant !== variant;
		if (didVariantChange) {
			void options.track('draft_reset', { reason: 'variant_change' });
			return await draftClient.createDraft(draftAuthorId, { metadata: defaultMetadata });
		}

		const didUserChange = currentDraft.metadata?.user?.id && viewer.user?.id !== currentDraft.metadata.user.id;
		if (didUserChange) {
			void options.track('draft_reset', { reason: 'user_change' });
			return await draftClient.createDraft(draftAuthorId, { metadata: defaultMetadata });
		}

		// track device changes
		const didDeviceChange = currentDraft.metadata?.deviceId && deviceId !== currentDraft.metadata.deviceId;
		if (didDeviceChange) {
			void options.track('draft_reset', { reason: 'device_change' });
		}

		// always ensure metadata has the latest user id/device ids
		const metadata = merge({}, currentDraft.metadata, { user: { id: viewer.user?.id }, deviceId });

		return { ...currentDraft, metadata };
	} catch (error) {
		// report and throw
		throw new Error(`Failed to find or create draft: ${(error as Error).message}`, { cause: error });
	}
}
